import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { MessageCircle, Send, Users, PhoneOff, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { HistorialEnvios, Stat } from './CampanaPortalPanel';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const BASE = `${API}/api/campana-portal/whatsapp`;

const VistaPrevia = ({ mensaje }) => (
  <div className="rounded-xl bg-[#e7f6e7] p-4 text-sm text-gray-800 leading-relaxed" data-testid="campana-wa-vista-previa">
    ¡Hola <b>María</b>! Te contactamos desde la administración del Colegio de Trabajadorxs Sociales de Lomas de Zamora. ¿Cómo estás? {mensaje} Si necesitás mas info, respondé este mensaje y nos contactamos con vos.
  </div>
);

export const CampanaWhatsappPanel = () => {
  const [data, setData] = useState(null);
  const [mensaje, setMensaje] = useState('');
  const [celular, setCelular] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const cargar = useCallback(async () => {
    const res = await axios.get(`${BASE}/resumen`, auth());
    setData(res.data);
    return res.data;
  }, []);

  useEffect(() => { cargar().then((d) => setMensaje(d.mensaje)); }, [cargar]);
  const enCurso = data?.envios?.some((e) => e.estado === 'en_curso');
  useEffect(() => {
    if (!enCurso) return undefined;
    const t = setInterval(cargar, 5000);
    return () => clearInterval(t);
  }, [enCurso, cargar]);

  const run = async (fn, okText) => {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg({ ok: true, text: okText }); await cargar(); }
    catch (err) { setMsg({ ok: false, text: err.response?.data?.detail?.toString() || 'Ocurrió un error' }); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="p-6 text-gray-500">Cargando campaña WhatsApp…</div>;
  const sinGuardar = mensaje.trim() !== data.mensaje;

  return (
    <div className="max-w-7xl mx-auto px-4 pb-6 space-y-6" data-testid="campana-wa-panel">
      <div className="bg-white rounded-xl border p-6 space-y-5">
        <div>
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-green-600" /> Campaña por WhatsApp
          </h2>
          <p className="text-sm text-gray-500 mt-1">Para los matriculados sin cuenta que no tienen email cargado. Usa la plantilla aprobada en Twilio; si responden, la conversación entra al CRM como “Campaña portal” para que la atienda una persona.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Stat icon={Users} label="Lo van a recibir" value={data.destinatarios} testid="campana-wa-destinatarios" />
          <Stat icon={PhoneOff} label="Celular cargado pero inválido" value={data.celular_invalido} testid="campana-wa-invalidos" />
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Texto libre del mensaje (va en una sola línea)</label>
            <textarea value={mensaje} onChange={(e) => setMensaje(e.target.value)} rows={5} maxLength={800} data-testid="campana-wa-mensaje-input" className="w-full px-3 py-2 border rounded-lg text-sm" />
            <button disabled={busy || !sinGuardar || mensaje.trim().length < 20} data-testid="campana-wa-guardar-btn"
              onClick={() => run(() => axios.put(`${BASE}/mensaje`, { mensaje }, auth()).then((r) => setMensaje(r.data.mensaje)), 'Mensaje guardado')}
              className="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 disabled:opacity-50"><Save className="w-4 h-4" /> Guardar texto</button>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Así lo recibe el matriculado</p>
            <VistaPrevia mensaje={mensaje} />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input value={celular} onChange={(e) => setCelular(e.target.value)} placeholder="Tu celular, ej. 11 5566 7788" data-testid="campana-wa-celular-prueba-input" className="flex-1 px-3 py-2 border rounded-lg text-sm" />
          <button disabled={busy || !celular || sinGuardar} data-testid="campana-wa-enviar-prueba-btn"
            onClick={() => run(() => axios.post(`${BASE}/prueba`, { celular }, auth()), `Prueba enviada a ${celular}`)}
            className="px-4 py-2 text-sm border border-green-600 text-green-700 rounded-lg hover:bg-green-50 disabled:opacity-50">Enviarme una prueba</button>
          <button disabled={busy || enCurso || sinGuardar || data.destinatarios === 0} onClick={() => setConfirmOpen(true)} data-testid="campana-wa-enviar-todos-btn"
            className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50"><Send className="w-4 h-4" /> Enviar a {data.destinatarios}</button>
        </div>
        {sinGuardar && <p className="text-xs text-amber-700" data-testid="campana-wa-sin-guardar">Guardá el texto antes de enviar.</p>}
        {msg && <p data-testid="campana-wa-mensaje" className={`text-sm ${msg.ok ? 'text-green-700' : 'text-red-600'}`}>{msg.text}</p>}
      </div>
      <HistorialEnvios envios={data.envios} testid="campana-wa-historial" />
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="campana-wa-confirm-dialog">
          <DialogHeader><DialogTitle>¿Enviar la campaña por WhatsApp?</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-600">Se va a mandar el WhatsApp a <b>{data.destinatarios}</b> matriculados sin cuenta y sin email. Cada mensaje tiene costo en Twilio y no se puede deshacer.</p>
          <DialogFooter>
            <button onClick={() => setConfirmOpen(false)} data-testid="campana-wa-confirm-cancel" className="px-4 py-2 text-sm border rounded-lg">Cancelar</button>
            <button data-testid="campana-wa-confirm-ok" className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg"
              onClick={() => { setConfirmOpen(false); run(() => axios.post(`${BASE}/enviar`, { confirmar: true }, auth()), 'Envío iniciado. Podés seguir el avance acá abajo.'); }}>Sí, enviar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
